import theme from 'config/theme';
import {Popup} from 'extensions/viewport/Popup';
import {Button} from 'interface/base/Button';
import {Icon} from 'interface/base/Icon';
import Typography from 'interface/base/Typography';
import {Fragment, useState} from 'react';
import {StyleSheet, View} from 'react-native';

import type {MetadataState} from '../types';

interface Props {
  metadata: MetadataState;
  visibleTemplateIds?: string[];
}

export function TemplatesOverflow({metadata, visibleTemplateIds = []}: Props) {
  const [hasMenu, setHasMenu] = useState(false);
  const {templates, getTemplateData, templateId} = metadata;

  // Get all template data
  const allTemplates = templates
    .map(template => getTemplateData(template.template_id))
    .filter(Boolean);

  // Filter to only show templates not already visible in the tabs
  const overflowTemplates = allTemplates.filter(
    template => template.id && !visibleTemplateIds.includes(template.id),
  );

  // If all templates fit in the tabs, don't show the overflow button
  if (overflowTemplates.length === 0) {
    return null;
  }

  const trigger = (
    <Button
      onPress={() => setHasMenu(true)}
      variant="secondary"
      overrides={{
        Button: {
          style: {
            padding: 0,
            width: 32,
            height: 32,
          },
        },
      }}
      startEnhancer={
        <Icon name="lucide:chevrons-right" color={theme.colors.neutral.$700} />
      }
    />
  );

  const update = (value: string) => {
    metadata.selectTemplate(value);
    setHasMenu(false);
  };

  return (
    <Popup
      isOpen={hasMenu}
      showBorder={false}
      triggerElement={trigger}
      customContainerStyle={styles.popupContainer}
      customPopupStyle={styles.customPopupStyle}
      close={() => setHasMenu(false)}>
      <Fragment>
        <Typography
          size="sm"
          variant="regular"
          color={theme.colors.neutral.$500}
          overrides={styles.title}>
          Select template:
        </Typography>
        <View style={styles.buttonGrid}>
          {overflowTemplates.map(tpl =>
            tpl.id ? (
              <Button
                key={tpl.id}
                variant={templateId === tpl.id ? 'primary' : 'outline'}
                onPress={() => update(tpl.id)}
                startEnhancer={({hovered}: {hovered: boolean}) => (
                  <Icon
                    name={tpl.icon}
                    color={
                      templateId === tpl.id
                        ? theme.colors.neutral.$0
                        : hovered
                          ? theme.colors.neutral.$500
                          : theme.colors.neutral.$300
                    }
                  />
                )}
                overrides={{
                  Button: {
                    style:
                      templateId === tpl.id
                        ? {...styles.templateButton, ...styles.activeButton}
                        : styles.templateButton,
                  },
                }}>
                <Typography
                  size="xs"
                  color={
                    templateId === tpl.id
                      ? theme.colors.neutral.$0
                      : theme.colors.neutral.$500
                  }>
                  {tpl.name}
                </Typography>
              </Button>
            ) : null,
          )}
        </View>
      </Fragment>
    </Popup>
  );
}

const styles = StyleSheet.create({
  title: {
    marginBottom: 16,
  },
  buttonGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    width: '100%',
  },
  templateButton: {
    minWidth: 'auto',
    justifyContent: 'flex-start',
  },
  activeButton: {
    backgroundColor: theme.colors.brand.$1_500,
  },
  popupContainer: {
    borderRadius: 16,
    padding: 16,
    width: 480,
    maxWidth: 480,
    minWidth: 480,
    shadowColor: 'rgb(28, 27, 34)',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    backgroundColor: theme.colors.neutral.$0,
    flexDirection: 'column',
  },
  customPopupStyle: {
    backgroundColor: 'transparent',
    paddingBottom: 8,
    paddingTop: 8,
    paddingHorizontal: 8,
  },
});
